import { NO_NICHE, jobTotalUnits } from "./jobs";
import { UNSORTED_NICHE_NAME } from "./niches";
import { targetProgress, furthestTarget, districtOf } from "./industryRollup";

// The Audits page (accounts with Industry Audits): which industries get a tab,
// which one is selected, and the shapes the panels draw (rings, graph, map
// dots, nudge). Pure: no React, no database, no browser.

// The industry key for Snapshots with no niche (niche ids are uuids).
export const NO_NICHE_KEY = "none";
export const MAX_TABS = 3;

// ---- Industries and tabs ----

// The account's industries from the Snapshots list, so the tabs show before
// the roll-up arrives: [{ key, nicheId, name, snapshots, running, special }].
// special: Unsorted or No niche (only ever in More, never rings).
export function industriesFromJobs(jobs) {
  const byKey = new Map();
  for (const job of jobs || []) {
    const key = job.niche_id || NO_NICHE_KEY;
    if (!byKey.has(key)) {
      const name = job.niche_id ? (job.niches && job.niches.name) || "Unnamed niche" : NO_NICHE;
      byKey.set(key, { key, nicheId: job.niche_id || null, name, snapshots: 0, running: 0, special: !job.niche_id || name === UNSORTED_NICHE_NAME });
    }
    const industry = byKey.get(key);
    industry.snapshots++;
    if (job.status === "running") industry.running++;
  }
  return [...byKey.values()];
}

// Industry keys in tab order: real niches by most searches gathered (from the
// roll-up), or by most Snapshots until it arrives; then Unsorted, then No niche.
// Niches missing from the roll-up (no completed searches) count as 0.
export function orderIndustries(industries, rollup) {
  const searches = new Map(((rollup && rollup.niches) || []).map(n => [n.nicheId, n.searches]));
  const score = i => (rollup ? searches.get(i.nicheId) || 0 : i.snapshots);
  const real = industries.filter(i => !i.special).sort((a, b) => score(b) - score(a) || a.name.localeCompare(b.name));
  const special = industries.filter(i => i.special).sort((a, b) => (a.key === NO_NICHE_KEY) - (b.key === NO_NICHE_KEY));
  return [...real, ...special].map(i => i.key);
}

// Keeps an earlier order (frozen once the roll-up arrived), adding any new
// industries at the end in their own order and dropping ones that are gone.
export function keepOrder(frozen, fresh) {
  if (!frozen) return fresh;
  const now = new Set(fresh);
  const kept = frozen.filter(k => now.has(k));
  return [...kept, ...fresh.filter(k => !kept.includes(k))];
}

// The selected industry: the asked-for key if this account has it, else the
// first in the order (or null when there are none). Never throws.
export function resolveIndustry(requested, order) {
  if (requested && order.includes(requested)) return requested;
  return order[0] || null;
}

// { tabs, more }: up to 3 real niches as tabs; everything else in More. A
// selected industry that isn't a tab (picked from More) takes the last tab.
export function industryTabs(order, industries, selected) {
  const special = new Set(industries.filter(i => i.special).map(i => i.key));
  const tabs = order.filter(k => !special.has(k)).slice(0, MAX_TABS);
  if (selected && !tabs.includes(selected)) {
    if (tabs.length === MAX_TABS) tabs[MAX_TABS - 1] = selected; else tabs.push(selected);
  }
  return { tabs, more: order.filter(k => !tabs.includes(k)) };
}

// ---- Page address: ?industry=<niche id | none> ----

export function parseAuditsAddress(search) {
  return new URLSearchParams(search || "").get("industry") || null;
}

export function buildAuditsAddress(key) {
  return key ? `?industry=${encodeURIComponent(key)}` : "";
}

// ---- Live list ----

// True when a Snapshot that was running before is now finished or stopped
// (or gone), so the roll-up should be loaded again.
export function finishedSince(before, after) {
  const now = new Map((after || []).map(j => [j.id, j.status]));
  return (before || []).some(j => j.status === "running" && now.get(j.id) !== "running");
}

// A running Snapshot's share done, 0-1.
export function runningShare(job) {
  const total = jobTotalUnits(job);
  return total > 0 ? Math.min(1, (job.completed_units || 0) / total) : 0;
}

// ---- Industry Audit panel ----

// The roll-up's entry for an industry, or an all-zero one for a niche in the
// list with no completed searches yet (so it still shows, with empty rings).
// Null for No niche (the roll-up never covers it).
export function nicheNumbers(industry, rollup) {
  if (!industry || !industry.nicheId || !rollup) return null;
  const found = rollup.niches.find(n => n.nicheId === industry.nicheId);
  if (found) return found;
  const counts = { searches: 0, keywords: 0, locations: 0 };
  const progress = targetProgress(counts, rollup.targets);
  return {
    nicheId: industry.nicheId, name: industry.name, unsorted: industry.special, snapshots: industry.snapshots,
    ...counts, advertisers: 0, estimated: false, insightSince: null, gatheringNow: 0, searchesThisWeek: 0,
    targets: rollup.targets, progress, unlocked: false, furthest: furthestTarget(progress),
    searchesOverTime: [], channelMix: [], locationsCovered: [],
  };
}

const NUDGE = {
  locations: ["Add", "location", "locations"],
  keywords: ["Add", "keyword", "keywords"],
  searches: ["Gather", "search", "searches"],
};

// "Add 6 more locations to unlock", from the target furthest from met;
// "All three targets met" when unlocked; null when there's nothing to say.
export function nudgeText(niche) {
  if (!niche || !niche.progress) return null;
  if (niche.unlocked) return "All three targets met";
  if (!niche.furthest) return null;
  const { done, target } = niche.progress[niche.furthest];
  const left = target - done;
  const [verb, one, many] = NUDGE[niche.furthest];
  return `${verb} ${left.toLocaleString("en-GB")} more ${left === 1 ? one : many} to unlock`;
}

// The three rings, outer to inner: searches, locations, keywords.
export const RING_ORDER = ["searches", "locations", "keywords"];
export const RING_RADII = [62, 47, 32];

// stroke-dasharray for a ring of radius r filled to share (0-1).
export function ringDash(share, r) {
  const c = 2 * Math.PI * r;
  return `${(Math.max(0, Math.min(1, share || 0)) * c).toFixed(1)} ${c.toFixed(1)}`;
}

// "Searches 34%, locations 20%, keywords 100%" for the rings' label.
export function ringLabel(progress) {
  if (!progress) return "Targets not loaded";
  const text = RING_ORDER.map(t => `${t} ${Math.floor(progress[t].percent * 100)}%`).join(", ");
  return text[0].toUpperCase() + text.slice(1);
}

// The cumulative searches graph in a 300 x 64 box: { line, area } paths, by
// date (so gaps show), or null with no searches. It starts from 0 the day
// before the first search, so even a single day of searches reads as a rise.
const DAY_MS = 24 * 60 * 60 * 1000;

export function growthPaths(points, width = 300, height = 64) {
  if (!points || points.length === 0) return null;
  const times = [Date.parse(points[0].day) - DAY_MS, ...points.map(p => Date.parse(p.day))];
  const totals = [0, ...points.map(p => p.total)];
  const first = times[0];
  const span = times[times.length - 1] - first;
  const max = totals[totals.length - 1] || 1;
  const xy = totals.map((total, i) => [((times[i] - first) / span) * width, height - 2 - (total / max) * (height - 6)]);
  const line = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  return { line, area: `${line} L${width} ${height} L0 ${height} Z` };
}

// ---- Coverage map ----

// The pins for a niche's coverage list on the street map:
// [{ district, lastSearchedAt, lat, lng, repeated }], only those with a position.
// offMap: how many counted locations have no pin.
export function mapDots(locationsCovered, locations) {
  const dots = (locationsCovered || [])
    .filter(l => Number.isFinite(l.lat) && Number.isFinite(l.lng))
    .map(l => ({ district: l.district, lastSearchedAt: l.lastSearchedAt, lat: l.lat, lng: l.lng, repeated: Boolean(l.repeated) }));
  return { dots, offMap: Math.max(0, (locations || 0) - dots.length) };
}

// A Snapshot's postcodes as districts, for lighting them up on the map:
// { districts: Set, main } where main is the business postcode's district
// when that was searched (so it has a pin), else the first postcode's.
// Null for no job.
export function jobDistricts(job) {
  if (!job) return null;
  const districts = new Set((job.locations || []).map(districtOf).filter(Boolean));
  const business = districtOf(job.business_postcode);
  const main = business && districts.has(business) ? business : districtOf((job.locations || [])[0]);
  return { districts, main };
}

// "1 Snapshot", "31 locations".
export function count(n, one, many) {
  return `${Number(n || 0).toLocaleString("en-GB")} ${n === 1 ? one : many}`;
}
